import { useState, useCallback, useRef } from "react";
import { Upload } from "lucide-react";

interface DropZoneProps {
  onFilesSelect: (files: File[]) => void;
  disabled?: boolean;
}

const DropZone = ({ onFilesSelect, disabled }: DropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items?.length > 0) setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) onFilesSelect(files);
    },
    [onFilesSelect, disabled]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onFilesSelect(files);
    e.target.value = "";
  };

  const inputId = "invoice-upload-input";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <label
      htmlFor={inputId}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-all ${
        disabled
          ? "pointer-events-none opacity-50"
          : isDragging
            ? "border-[hsl(var(--brand-green))] bg-accent"
            : "border-border hover:border-[hsl(var(--brand-green))]/40 hover:bg-muted/50"
      }`}
    >
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        multiple
        onChange={handleInputChange}
        className="sr-only"
        disabled={disabled}
      />
      <Upload
        className={`pointer-events-none mb-3 h-10 w-10 ${
          isDragging ? "text-[hsl(var(--brand-green))]" : "text-muted-foreground"
        }`}
      />
      <p className="pointer-events-none text-sm font-medium text-foreground">
        Arrastra tus facturas aquí
      </p>
      <p className="pointer-events-none mt-1 text-xs text-muted-foreground">
        o haz clic para seleccionar PDF, PNG o JPG
      </p>
    </label>
  );
};

export default DropZone;
